import React, { useRef, useEffect, useState } from 'react';
import { string } from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useQueries } from 'react-query';
import { Alert, Radio } from '@nextiva/next-ui-components';

import Hint from 'components/VoicemailHint';
import UserTable from 'components/UserTable';
import SectionSaveCancel from 'components/VoicemailSectionSaveCancel';
import { Skeleton } from 'components/Skeleton';
import { useSnackbar } from 'hooks';
import { HuntGroupService } from 'services';
import {
  Container,
  Header,
  Here,
  Title,
  Divider,
  FormControlComponent,
  RadioBtn,
  FormLabelCss,
  SelectComponent,
  SelectOptions,
  Label,
  CheckboxComponent,
} from './CallDistribution.styles.ts';

const {
  getCallForwardUnanswered,
  getHuntGroupCallDistro,
  putHuntGroupCallDistro,
} = HuntGroupService;

const callTimeAlert = (
  <div>
    <Alert severity="info">
      Callers will go to voicemail after ringing each other for the specified
      time
    </Alert>
  </div>
);

const CallTimeDialog = ({ forwardTimeoutSeconds }) => (
  <div>
    <Alert severity="info">
      After &nbsp;<b>{forwardTimeoutSeconds} seconds</b>&nbsp; of ringing
      everyone at once, callers will follow the call forward unanswered settings
      found &nbsp;
      <Here
        to="forwarding"
        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-Link"
      >
        here
      </Here>
      .
    </Alert>
  </div>
);

const RING_EVERYONE_OPTIONS = [
  5,
  10,
  15,
  20,
  25,
  30,
  35,
  40,
  45,
  50,
  60,
  120,
  180,
].map((time) => ({
  label: `${time} seconds`,
  value: time,
}));

function displayCallInfo(
  unanswered,
  showDistroType,
  onCallTimeChange,
  forwardTimeoutSeconds,
) {
  if (unanswered) {
    return <CallTimeDialog forwardTimeoutSeconds={forwardTimeoutSeconds} />;
  }
  if (showDistroType) {
    return callTimeAlert;
  }

  return (
    <div>
      <FormControlComponent component="fieldset" variant="outlined">
        <FormLabelCss component="legend">
          <Hint tool="RING EVERYONE FOR" />
        </FormLabelCss>
        <SelectComponent
          value={forwardTimeoutSeconds}
          onChange={onCallTimeChange}
          data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-SelectRing"
        >
          {RING_EVERYONE_OPTIONS.map((time) => (
            <SelectOptions key={time.value} value={time.value}>
              {time.label}
            </SelectOptions>
          ))}
        </SelectComponent>
      </FormControlComponent>
      {callTimeAlert}
    </div>
  );
}

const RING_NUMBER_OPTIONS = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
].map((rings) => ({
  label: `${rings} ${rings === 1 ? 'ring' : 'rings'}`,
  value: rings,
}));

const distroTypes = {
  SIMULTANEOUS: 'Simultaneous',
  ROUND_ROBIN: 'Circular',
  EQUALLY: 'Uniform',
  FIRST_TO_LAST: 'Regular',
};

const DISTRO_OPTIONS = [
  {
    label: 'Round-robin',
    type: 'ROUND_ROBIN',
  },
  {
    label: 'First to last',
    type: 'FIRST_TO_LAST',
  },
  {
    label: 'Equally',
    type: 'EQUALLY',
  },
];

export const CallDistribution = ({ huntGroupId = '' }) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState,
    reset,
    getValues,
  } = useForm();
  const [loading, setLoading] = useState(true);
  const [
    {
      data: groupCallDistro,
      isLoading: groupCallDistroLoading,
      remove: removeGroupCallDistro,
      isError,
    },
    {
      data: forwardUnanswered,
      isLoading: forwardUnansweredLoading,
      remove: removeForwardUnanswered,
      isError: isErrorForwardUnanswered,
    },
  ] = useQueries([
    {
      queryKey: ['huntGroupCallDistro', huntGroupId],
      queryFn: () => getHuntGroupCallDistro(huntGroupId),
    },
    {
      queryKey: ['callForwardUnanswered', huntGroupId],
      queryFn: () => getCallForwardUnanswered(huntGroupId),
    },
  ]);
  const [showDistroType, setShowDistroType] = useState(true);
  const [distroType, setDistroType] = useState(distroTypes.FIRST_TO_LAST);
  const [unanswered, setUnanswered] = useState(false);
  const [callWaiting, setCallWaiting] = useState(false);
  const [userData, setUserData] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showSaveCancel, setShowSaveCancel] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [ringNumber, setRingNumber] = useState('1');
  const [forwardTimeoutSeconds, setForwardTimeoutSeconds] = useState(30);

  const formRef = useRef(null);
  const initFormState = useRef(null);

  const setSnackbar = useSnackbar();

  const ringTypeConversion = {
    ALL: () => {
      setValue('distributionType', distroTypes.SIMULTANEOUS);
      setShowDistroType(false);
    },
    EACH: () => {
      setValue('distributionType', distroType);
      setValue('ringDuration', ringNumber);
      setShowDistroType(true);
    },
  };

  const setInitRingType = {
    Simultaneous: () => {
      setValue('ringType', 'ALL');
      setShowDistroType(false);
    },
    Circular: (ringDuration) => {
      setValue('ringType', 'EACH');
      setShowDistroType(true);
      setRingNumber(`${ringDuration}`);
    },
    Uniform: (ringDuration) => {
      setValue('ringType', 'EACH');
      setShowDistroType(true);
      setRingNumber(`${ringDuration}`);
    },
    Regular: (ringDuration) => {
      setValue('ringType', 'EACH');
      setShowDistroType(true);
      setRingNumber(`${ringDuration}`);
    },
  };

  useEffect(() => {
    if (!showSaveCancel && formState.isDirty) setShowSaveCancel(true);
    if (formState.isDirty) setSaveDisabled(false);
  }, [formState.isDirty]);

  useEffect(() => {
    if (!huntGroupId || forwardUnansweredLoading || groupCallDistroLoading) {
      return;
    }
    if (isErrorForwardUnanswered || isError) {
      setLoading(false);
      return;
    }
    setForwardTimeoutSeconds(forwardUnanswered.forwardTimeoutSeconds);
    setUnanswered(!!forwardUnanswered.forwardToPhoneNumber);
    const {
      callWaitingAllowed,
      distributionType,
      ringDuration,
      useSystemHuntGroupClIdSetting,
      users,
    } = groupCallDistro;
    // callWaitingAllowed checkbox needs its default value set so that it reflects the initial value.
    setCallWaiting(callWaitingAllowed);
    setValue('callWaitingAllowed', callWaitingAllowed);
    // distributionType has a data value('Simultaneous') which is not in the dropdown.
    // The last value in the dropdown is tracked by component state. This enables the
    // interface to know what to set the form state too when the distributionType is
    // switched to 'Simultaneous' and back to the dropdown. The dropdown is hidden when
    // the distributionType is set to 'Simultaneous'. This behavior is controlled by
    // the distribution radio group at the top of the form.
    setInitRingType[distributionType](ringDuration);
    setValue('distributionType', distributionType);
    setValue(
      'ringDuration',
      distributionType === distroTypes.SIMULTANEOUS
        ? forwardUnanswered.forwardTimeoutSeconds
        : ringDuration,
    );
    // the caller id radio group is driven by the useSystemHuntGroupClIdSetting key on
    // response obj which is a boolean.
    setValue('useSystemHuntGroupClIdSetting', useSystemHuntGroupClIdSetting);
    setValue(
      'callerId',
      useSystemHuntGroupClIdSetting ? 'CALLER_TEAM' : 'CALLER_ONLY',
    );
    // users does not have a input field and is tracked in component state and form state.
    setValue('users', users);
    setUserData(users);
    // track the forms initial state so it can be reset onCancel
    initFormState.current = getValues();
    setLoading(false);
  }, [
    huntGroupId,
    forwardUnanswered,
    forwardUnansweredLoading,
    isErrorForwardUnanswered,
    groupCallDistro,
    groupCallDistroLoading,
    isError,
  ]);

  const onRingTypeClick = (type) => () => {
    setValue('ringType', type);
    ringTypeConversion[type]();
  };

  const onCallerIdType = (type) => () => {
    setValue('callerId', type);
    setValue('useSystemHuntGroupClIdSetting', type === 'CALLER_TEAM');
  };

  const onDistroTypeClick = (type) => () => {
    setDistroType(type);
  };

  const onSubmit = (data) => {
    // ringType is not a part of the request object
    delete data.ringType;
    // The Backend expects a users data, this code checks if there is no users property in the request object it will add empty users array.
    const checkUsersData = data.hasOwnProperty('users');
    if (!checkUsersData) {
      data.users = [];
    }
    putHuntGroupCallDistro(huntGroupId, data)
      .then(() => {
        setSnackbar({
          message: 'Saved. Settings have been updated successfully.',
          severity: 'success',
        });
        setShowSpinner(false);
        const userSetValues = getValues();
        initFormState.current = userSetValues;
        reset(userSetValues);
        removeGroupCallDistro();
        removeForwardUnanswered();
        setShowSaveCancel(false);
      })
      .catch(() => {
        setSnackbar({
          message:
            "Sorry, we weren't able to save your changes. Please contact support.",
          severity: 'error',
        });
        setShowSpinner(false);
        reset(initFormState.current);
        setShowSaveCancel(false);
      });
  };

  const onCancel = () => {
    const { useSystemHuntGroupClIdSetting } = initFormState.current;
    setInitRingType[initFormState.current.distributionType]();
    setValue(
      'callerId',
      useSystemHuntGroupClIdSetting ? 'CALLER_TEAM' : 'CALLER_ONLY',
    );
    if (initFormState.current.distributionType !== 'Simultaneous')
      setRingNumber(initFormState.current.ringDuration);
    if (!showDistroType) setRingNumber(initFormState.current.ringDuration);
    reset(initFormState.current);
    setSaveDisabled(true);
    setUserData(initFormState.current.users);
    setShowSaveCancel(false);
  };

  const onSave = () => {
    setShowSpinner(true);
    setSaveDisabled(true);
    formRef.current.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true }),
    );
  };

  const onRingNumberChange = (e) => {
    setRingNumber(e.target.value);
    setValue('ringDuration', e.target.value, { shouldDirty: true });
  };

  const onCallTimeChange = (e) => {
    const seconds = e.target.value;
    setForwardTimeoutSeconds(seconds);
    setValue('ringDuration', seconds, { shouldDirty: true });
    setShowSaveCancel(true);
  };

  const onSetUserData = (users) => {
    setUserData(users);
    setValue('users', users, { shouldDirty: true });
  };

  return loading ? (
    <Skeleton />
  ) : (
    <>
      <Container>
        <Header data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-Header">
          Call Distribution
        </Header>
        <Divider />
        <Title data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-Title">
          Distribution settings
        </Title>
        <div>
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit)}
            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-Form"
          >
            <FormControlComponent component="fieldset">
              <FormLabelCss data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-DistributionHeader">
                <Hint
                  tool="DISTRIBUTION"
                  tip="This is the ring pattern calls will follow."
                />
              </FormLabelCss>
              <Controller
                name="ringType"
                control={control}
                defaultValue="EACH"
                render={({ field: { name, value, onChange, onBlur, ref } }) => {
                  return (
                    <RadioBtn
                      aria-label="distribution"
                      ref={ref}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RadioGroup"
                    >
                      <Label
                        value="ALL"
                        control={
                          <Radio
                            onClick={onRingTypeClick('ALL')}
                            disableRipple
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingAllRadioButton',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingAll"
                          />
                        }
                        label="Ring everyone at once"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingAllLabel"
                      />
                      <Label
                        value="EACH"
                        control={
                          <Radio
                            onClick={onRingTypeClick('EACH')}
                            disableRipple
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingOneUserRadioButton',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-Radio"
                          />
                        }
                        label="Ring one user at a time"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingOneUserLabel"
                      />
                    </RadioBtn>
                  );
                }}
              />
            </FormControlComponent>
            {showDistroType && (
              <div styles={{ display: 'flex', flexDirection: 'row' }}>
                <FormControlComponent component="fieldset" variant="outlined">
                  <FormLabelCss data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-DistributionTypeHeader">
                    <Hint
                      tool="DISTRIBUTION TYPE"
                      tip={
                        <div>
                          <b>First to last</b> rings one phone at a time always
                          starting with the first person (A-B-C). Choose{' '}
                          <b>Round-robin</b> if you&#39;d like to make sure your
                          employees are all offered the same opportunity to
                          answer calls (first call goes to A, second goes to B,
                          third goes to C, etc.). <b>Equally</b> will ring the
                          phone that has been idle longest in order to spread
                          calls to employees equally.
                        </div>
                      }
                    />
                  </FormLabelCss>
                  <Controller
                    name="distributionType"
                    control={control}
                    defaultValue=""
                    render={({ field }) => {
                      return (
                        <SelectComponent
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'center',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'center',
                            },
                          }}
                          {...field}
                        >
                          {DISTRO_OPTIONS.map((option) => {
                            return (
                              <SelectOptions
                                key={option.type}
                                value={distroTypes[option.type]}
                                onClick={onDistroTypeClick(
                                  distroTypes[option.type],
                                )}
                                data-testid={`PlatformAdminUI-TeamVoiceSettings-CallDistribution-DistributionTypeMenu-${option.label}`}
                              >
                                {option.label}
                              </SelectOptions>
                            );
                          })}
                        </SelectComponent>
                      );
                    }}
                  />
                </FormControlComponent>
                <FormControlComponent component="fieldset" variant="outlined">
                  <FormLabelCss
                    component="legend"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingEachUserForHeader"
                  >
                    <Hint
                      tool="RING EACH USER FOR"
                      tip="We suggest keeping the number of rings at 6 or fewer."
                    />
                  </FormLabelCss>
                  <SelectComponent
                    value={ringNumber}
                    onChange={onRingNumberChange}
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                      style: { maxHeight: '308px' },
                    }}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingNumber"
                  >
                    {RING_NUMBER_OPTIONS.map((option) => {
                      return (
                        <SelectOptions
                          key={option.label}
                          value={option.value}
                          data-testid={`PlatformAdminUI-TeamVoiceSettings-CallDistribution-RingNumberOptions-${option.label}`}
                        >
                          {option.label}
                        </SelectOptions>
                      );
                    })}
                  </SelectComponent>
                </FormControlComponent>
              </div>
            )}
            {displayCallInfo(
              unanswered,
              showDistroType,
              onCallTimeChange,
              forwardTimeoutSeconds,
            )}
            <div>
              <FormControlComponent component="fieldset">
                <Controller
                  name="callWaitingAllowed"
                  control={control}
                  defaultValue={callWaiting}
                  render={({
                    field: { name, value, onChange, onBlur, ref },
                  }) => {
                    return (
                      <Label
                        control={
                          <CheckboxComponent
                            name={name}
                            ref={ref}
                            checked={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            disableRipple
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-CallDistribution-AllowCallWaitingCheckBox',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallWaiting"
                          />
                        }
                        label="Allow call waiting on users"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-AllowCallWaitingLabel"
                      />
                    );
                  }}
                />
              </FormControlComponent>
            </div>
            <FormControlComponent component="fieldset">
              <FormLabelCss
                component="legend"
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallerIDDisplayLabel"
              >
                <Hint tool="CALLER ID DISPLAY" />
              </FormLabelCss>
              <Controller
                name="callerId"
                control={control}
                defaultValue="CALLER_TEAM" // data not in return object
                render={({ field }) => {
                  return (
                    <RadioBtn aria-label="distribution" {...field}>
                      <Label
                        value="CALLER_TEAM"
                        control={
                          <Radio
                            onClick={onCallerIdType('CALLER_TEAM')}
                            disableRipple
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallerAndTeamNameRadioButton',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallerTeam"
                          />
                        }
                        label="Caller & team name"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallerAndTeamNameLabel"
                      />
                      <Label
                        value="CALLER_ONLY"
                        control={
                          <Radio
                            onClick={onCallerIdType('CALLER_ONLY')}
                            disableRipple
                            inputProps={{
                              'data-testid':
                                'PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallersNameRadioButton',
                            }}
                            data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallerOnly"
                          />
                        }
                        label="Caller's name only"
                        data-testid="PlatformAdminUI-TeamVoiceSettings-CallDistribution-CallersNameLabel"
                      />
                    </RadioBtn>
                  );
                }}
              />
            </FormControlComponent>
          </form>
        </div>
        {showDistroType && (
          <UserTable
            users={userData}
            setUserData={onSetUserData}
            order={showDistroType}
            setValue={setValue}
          />
        )}
      </Container>
      <SectionSaveCancel
        onCancel={onCancel}
        onSave={onSave}
        open={showSaveCancel}
        saveDisabled={saveDisabled}
        showSpinner={showSpinner}
      />
    </>
  );
};

CallDistribution.propTypes = {
  huntGroupId: string,
};

export default CallDistribution;
